import { Form } from "@unform/web";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Building, HandCoins } from "lucide-react";
import FormHeader from "~/components/RegisterForm/FormHeader";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import Input from "~/components/RegisterForm/Input";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormLoading from "~/components/RegisterForm/FormLoading";
import Preview from "~/components/Preview";
import api from "~/services/api";
import { format, parseISO, isValid } from "date-fns";

export const InputContext = createContext({});

function formatDate(date) {
  if (!date) return "-";
  const parsedDate = parseISO(date);
  return isValid(parsedDate) ? format(parsedDate, "MM/dd/yyyy") : "-";
}

function formatCurrency(value) {
  const number = parseFloat(value || 0);
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

function getLatestResolution(receivable) {
  return receivable.financialHoldResolutions?.[0] || null;
}

function resolutionStatus(resolution) {
  if (!resolution) return "Blocking";
  const today = format(new Date(), "yyyy-MM-dd");
  return resolution.grace_until >= today
    ? "Promise Active"
    : "Promise Expired";
}

export default function PagePreview({ access, id, Context, defaultFormType = "full" }) {
  const { handleOpened } = useContext(Context);
  const [pageData, setPageData] = useState({
    student: null,
    receivables: [],
    loaded: false,
  });
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const generalForm = useRef();

  function handleCloseForm() {
    handleOpened(null);
  }

  useEffect(() => {
    async function fetchOverdueReceivables() {
      try {
        const { data } = await api.get(
          `/students/${id}/overdue-receivables`
        );
        setPageData({
          student: data.student,
          receivables: data.receivables || [],
          loaded: true,
        });
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
    if (id && id !== "new") {
      fetchOverdueReceivables();
    }
  }, [id]);

  const studentName = pageData.student
    ? `${pageData.student.name} ${pageData.student.last_name}`
    : "";

  return (
    <Preview fullscreen={fullscreen} formType={defaultFormType}>
      {pageData.loaded ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-32 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <Building size={16} /> General
            </RegisterFormMenu>
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="receivables"
            >
              <HandCoins size={16} /> Receivables
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <Form ref={generalForm} onSubmit={() => {}} className="w-full">
              <InputContext.Provider
                value={{
                  id,
                  generalForm,
                  fullscreen,
                  setFullscreen,
                  successfullyUpdated: true,
                  handleCloseForm,
                }}
              >
                <FormHeader
                  access={access}
                  title={studentName}
                  registry={{}}
                  InputContext={InputContext}
                  disableSave
                />

                <InputLineGroup
                  title="GENERAL"
                  activeMenu={activeMenu === "general"}
                >
                  <InputLine title="Student">
                    <Input
                      type="text"
                      name="student_name"
                      readOnly
                      grow
                      title="Student Name"
                      defaultValue={studentName}
                      InputContext={InputContext}
                    />
                    <Input
                      type="text"
                      name="registration_number"
                      readOnly
                      grow
                      title="Registration Number"
                      defaultValue={pageData.student?.registration_number}
                      InputContext={InputContext}
                    />
                  </InputLine>
                  <InputLine title="Overdue Information">
                    <Input
                      type="text"
                      name="has_overdue_invoice"
                      readOnly
                      grow
                      title="Has Overdue Invoice"
                      defaultValue={
                        pageData.student?.has_overdue_invoice ? "Yes" : "No"
                      }
                      InputContext={InputContext}
                    />
                    <Input
                      type="text"
                      name="overdue_invoice_flagged_at"
                      readOnly
                      grow
                      title="Overdue Since"
                      defaultValue={formatDate(
                        pageData.student?.overdue_invoice_flagged_at
                      )}
                      InputContext={InputContext}
                    />
                  </InputLine>
                </InputLineGroup>

                <InputLineGroup
                  title="RECEIVABLES"
                  activeMenu={activeMenu === "receivables"}
                >
                  <InputLine title="Overdue Receivables">
                    {pageData.receivables.length === 0 ? (
                      <p className="text-xs text-gray-400 py-2">
                        No overdue receivables found.
                      </p>
                    ) : (
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="text-left px-2 py-1">Invoice</th>
                            <th className="text-left px-2 py-1">Type</th>
                            <th className="text-left px-2 py-1">
                              Due Date
                            </th>
                            <th className="text-right px-2 py-1">Total</th>
                            <th className="text-right px-2 py-1">
                              Balance
                            </th>
                            <th className="text-left px-2 py-1">Status</th>
                            <th className="text-left px-2 py-1">
                              Promise Until
                            </th>
                            <th className="text-left px-2 py-1">
                              Hold Status
                            </th>
                            <th className="text-left px-2 py-1">Memo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageData.receivables.map((receivable) => {
                            const resolution = getLatestResolution(receivable);
                            return (
                              <tr
                                key={receivable.id}
                                className="border-b even:bg-gray-50"
                              >
                              <td className="px-2 py-1">
                                {receivable.invoice_number
                                  ?.toString()
                                  .padStart(6, "0")}
                              </td>
                              <td className="px-2 py-1">
                                {receivable.type_detail || receivable.type}
                              </td>
                              <td className="px-2 py-1">
                                {formatDate(receivable.due_date)}
                              </td>
                              <td className="px-2 py-1 text-right">
                                {formatCurrency(receivable.total)}
                              </td>
                              <td className="px-2 py-1 text-right">
                                {formatCurrency(receivable.balance)}
                              </td>
                              <td className="px-2 py-1">
                                <span
                                  className={
                                    receivable.status === "Paid"
                                      ? "text-green-600 font-semibold"
                                      : "text-red-500 font-semibold"
                                  }
                                >
                                  {receivable.status}
                                </span>
                              </td>
                                <td className="px-2 py-1">
                                  {resolution
                                    ? formatDate(resolution.grace_until)
                                    : "-"}
                                </td>
                                <td className="px-2 py-1">
                                  {resolutionStatus(resolution)}
                                </td>
                                <td
                                  className="max-w-64 truncate px-2 py-1"
                                  title={resolution?.memo || ""}
                                >
                                  <span>{resolution?.memo || "-"}</span>
                                  {resolution?.createdBy?.name && (
                                    <span className="block text-gray-400">
                                      By {resolution.createdBy.name}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </InputLine>
                </InputLineGroup>
              </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : (
        <FormLoading />
      )}
    </Preview>
  );
}
